import React, { ReactNode } from 'react';
import { Card, Typography, Statistic, Row, Col } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import styles from './DashboardSectionCard.module.css';

const { Title, Text } = Typography;

// ============================================================================
// TYPES
// ============================================================================

interface MetricItem {
  label: string;
  value: string | number;
  suffix?: string;
  prefix?: string;
  trend?: 'up' | 'down' | 'neutral';
}

interface DashboardSectionCardProps {
  title: string;
  icon?: ReactNode;
  description?: string;
  onClick?: () => void;
  metrics?: MetricItem[];
  action?: ReactNode;
  loading?: boolean;
  className?: string;
  children?: ReactNode;
  /** Compact mode for sidebar widgets */
  compact?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const DashboardSectionCard: React.FC<DashboardSectionCardProps> = ({
  title,
  icon,
  description,
  onClick,
  metrics,
  action,
  loading = false,
  className,
  children,
  compact = false,
}) => {
  const isClickable = !!onClick;

  return (
    <Card
      className={`${styles.card} ${isClickable ? styles.clickable : ''} ${compact ? styles.compact : ''} ${className || ''}`}
      loading={loading}
      onClick={onClick}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          {icon && <span className={styles.icon}>{icon}</span>}
          <Title level={4} className={styles.title}>
            {title}
          </Title>
        </div>
        {isClickable && (
          <RightOutlined className={styles.arrow} />
        )}
      </div>

      {/* Description */}
      {description && (
        <Text className={styles.description}>{description}</Text>
      )}

      {/* Metrics */}
      {metrics && metrics.length > 0 && (
        <Row gutter={[16, 16]} className={styles.metricsRow}>
          {metrics.map((metric, index) => (
            <Col key={index} span={Math.floor(24 / metrics.length)}>
              <div className={styles.metric}>
                <Statistic
                  title={metric.label}
                  value={metric.value}
                  prefix={metric.prefix}
                  suffix={metric.suffix}
                  className={`${styles.statistic} ${metric.trend ? styles[metric.trend] : ''}`}
                />
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* Children Content */}
      {children && <div className={styles.content}>{children}</div>}

      {/* Custom Action */}
      {action && <div className={styles.actionArea}>{action}</div>}
    </Card>
  );
};

export default DashboardSectionCard;
